import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import Reveal from '../ui/Reveal'
import RevealImage from '../ui/RevealImage'
import { REVEAL_STAGGER } from '../../lib/revealMotion'
import { Link } from 'react-router-dom'
import {
  ArrowRightIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { useLanguage } from '../../contexts/LanguageContext'
import { getFeaturedPosts } from '../../lib/supabaseApi'
import { useLazyWhenVisible } from '../../lib/useLazyWhenVisible'
import { postCategoryName, postCoverUrl } from '../../utils/postImages'

export default function NewsSection() {
  const { t, locale } = useLanguage()
  const [posts, setPosts] = useState([])
  const [hasFetched, setHasFetched] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const sectionRef = useRef(null)
  const shouldFetch = useLazyWhenVisible(sectionRef)

  const localeTag = locale === 'pt' ? 'pt-BR' : locale === 'fr' ? 'fr-FR' : 'en-US'

  const formatDate = (date) => {
    if (!date) return 'N/D'
    return new Date(date).toLocaleDateString(localeTag, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  useEffect(() => {
    if (!shouldFetch) return undefined

    const fetchData = async () => {
      try {
        const data = await getFeaturedPosts(6)
        setPosts(data)
      } catch (error) {
        console.error('Error fetching news data:', error)
        setPosts([])
      } finally {
        setHasFetched(true)
      }
    }
    fetchData()
  }, [shouldFetch])

  useEffect(() => {
    if (posts.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % Math.min(posts.length, 6))
      }, 5000)
      return () => clearInterval(interval)
    }
    return undefined
  }, [posts.length])

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.min(posts.length, 6))
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.min(posts.length, 6)) % Math.min(posts.length, 6))
  }

  const featuredPosts = posts.slice(0, 6)

  return (
    <section ref={sectionRef} className="bg-transparent">
      <div className="public-band py-16 px-4 sm:px-6 lg:px-8">
        <div className="container-max">
          <div className="text-flow-justify mb-12 max-w-3xl text-left sm:mb-14">
            <Reveal>
              <div className="mb-8 flex items-center gap-4">
                <span className="h-px w-10 shrink-0 bg-brand-navy/25" aria-hidden />
                <span className="section-label">{t('newsHome.eyebrow')}</span>
              </div>
            </Reveal>
            <Reveal delay={REVEAL_STAGGER}>
              <h2 className="font-heading text-3xl font-bold leading-tight text-brand-navy md:text-4xl">
                {t('newsHome.h2a')}
                <span className="text-brand-green">{t('newsHome.h2b')}</span>
              </h2>
            </Reveal>
            <Reveal delay={REVEAL_STAGGER * 2}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                {t('newsHome.intro')}
              </p>
            </Reveal>
          </div>

          {!shouldFetch ? null : !hasFetched ? (
            <div className="relative">
              <div className="animate-pulse overflow-hidden border border-slate-200 bg-white">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="h-56 bg-slate-200 sm:h-64 lg:h-80" />
                  <div className="space-y-4 p-6 sm:p-8">
                    <div className="h-3 w-24 bg-slate-200" />
                    <div className="h-8 w-4/5 bg-slate-200" />
                    <div className="h-4 w-36 bg-slate-200" />
                    <div className="h-10 w-32 bg-slate-200" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              {featuredPosts.length > 0 ? (
                <>
                  <div className="relative overflow-hidden border border-slate-200 bg-white">
                    <motion.div
                      className="flex transition-transform duration-500 ease-in-out"
                      style={{
                        transform: `translateX(-${currentSlide * 100}%)`,
                      }}
                    >
                      {featuredPosts.map((article) => {
                        const categoryLabel = postCategoryName(article)
                        const cover = postCoverUrl(article)
                        return (
                        <div key={article.id} className="w-full shrink-0">
                          <motion.article
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="group overflow-hidden bg-white"
                          >
                            <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-stretch">
                              <Link to={`/blog/${article.slug}`} className="block">
                                <RevealImage
                                  src={cover}
                                  alt={article.title}
                                  wrapperClassName="h-full min-h-[14rem] overflow-hidden border-b border-slate-200 bg-slate-100 lg:min-h-[20rem] lg:border-b-0 lg:border-r lg:border-slate-200"
                                  className="h-56 w-full rounded-none object-cover transition-transform duration-500 group-hover:scale-[1.02] sm:h-64 lg:h-full lg:min-h-[20rem]"
                                  loading="lazy"
                                />
                              </Link>

                              <div className="flex flex-col p-6 text-left sm:p-8">
                                <p className="section-label">{categoryLabel}</p>

                                <h3 className="mt-2 font-heading text-2xl font-semibold leading-snug text-brand-navy transition-colors duration-200 group-hover:text-brand-blue md:text-3xl">
                                  <Link to={`/blog/${article.slug}`}>{article.title}</Link>
                                </h3>

                                <div className="mt-4 inline-flex items-center text-sm text-slate-500">
                                  <CalendarIcon className="mr-2 h-4 w-4 shrink-0" aria-hidden />
                                  <span>{formatDate(article.publishedAt)}</span>
                                </div>

                                <Link
                                  to={`/blog/${article.slug}`}
                                  className="mt-6 inline-flex w-fit items-center gap-2 rounded-none bg-brand-navy px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#0c1a30]"
                                >
                                  {t('newsHome.readMore')}
                                  <ArrowRightIcon
                                    className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
                                    aria-hidden
                                  />
                                </Link>
                              </div>
                            </div>
                          </motion.article>
                        </div>
                      )})}
                    </motion.div>
                  </div>

                  <div className="mt-8 flex items-center justify-between gap-4">
                    <button
                      type="button"
                      onClick={prevSlide}
                      aria-label={t('newsHome.prev')}
                      className="group flex h-11 w-11 shrink-0 items-center justify-center border border-slate-300 bg-white text-brand-navy shadow-sm outline-none transition-colors transition-transform duration-200 hover:bg-slate-50 active:scale-[0.97] active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 rounded-none"
                    >
                      <ChevronLeftIcon className="h-5 w-5 transition-transform duration-200 group-hover:-translate-x-0.5" />
                    </button>

                    <div className="flex flex-wrap justify-center gap-2">
                      {featuredPosts.map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setCurrentSlide(index)}
                          aria-label={t('newsHome.goTo', { n: String(index + 1) })}
                          aria-current={currentSlide === index ? 'true' : undefined}
                          className={`h-2.5 w-2.5 rounded-full outline-none transition-all duration-200 active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 ${
                            currentSlide === index
                              ? 'scale-125 bg-brand-navy'
                              : 'bg-slate-300 hover:bg-slate-400'
                          }`}
                        />
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={nextSlide}
                      aria-label={t('newsHome.next')}
                      className="group flex h-11 w-11 shrink-0 items-center justify-center border border-slate-300 bg-white text-brand-navy shadow-sm outline-none transition-colors transition-transform duration-200 hover:bg-slate-50 active:scale-[0.97] active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 rounded-none"
                    >
                      <ChevronRightIcon className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </button>
                  </div>

                </>
              ) : (
                <div className="py-12 text-center">
                  <p className="text-gray-400">{t('newsHome.empty')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
